import React, {useEffect, useState} from 'react'
import {Box, Flex, Text, useMultiStyleConfig} from '@chakra-ui/react'
import {useIntl, FormattedMessage, defineMessages} from 'react-intl'
import Search from '../../components/search'
import ProductRegistrationImageContainer from './partials/product-registration-image-container'
import useNavigation from '@salesforce/retail-react-app/app/hooks/use-navigation'
import routingObject from '../../utils/static-routes'
import ProductRegistrationHeader from './partials/product-registration-header'
import ProductRegistrationStepper from './partials/product-registration-stepper'
import {Helmet} from 'react-helmet'
import {useProductRegistrationData} from '../../custom-hooks/use-product-registration-data'
import {BRAND_KEY} from '../../brand'

const ProductSearch = () => {
    const styles = useMultiStyleConfig('ProductRegistration', {variant: 'productSearch'})
    const [searchResults, setSearchResults] = useState([])
    const [suggestionRegistration, setSuggestionRegistration] = useState([])
    const [productSelect, setProductSelect] = useState('')
    const navigate = useNavigation()

    const isKenwood = BRAND_KEY === 'kenwood'
    const isBraun = BRAND_KEY === 'braun'
    const isNutribullet = BRAND_KEY === 'nutribullet'

    const labels = defineMessages({
        kenwood_sku: {
            id: 'product_registration.sku_examples.kenwood',
            defaultMessage:
                'Examples of valid SKU codes: <i>0W22210041, 0W20011423, 0W22011080</i>',
        },
        braun_sku: {
            id: 'product_registration.sku_examples.braun',
            defaultMessage:
                'Examples of valid SKU codes: <i>0X22111581, 0X17900008, 0128805009</i>',
        },
        nutribullet_sku: {
            id: 'product_registration.sku_examples.nutribullet',
            defaultMessage:
                'Examples of valid SKU codes: <i>0C22500004, 0C22300099, 0C22300092</i>',
        },
        delonghi_sku: {
            id: 'product_registration.sku_examples',
            defaultMessage:
                'Examples of valid SKU codes: <i>0132250242, 0132127020, 0151455005</i>',
        },
        kenwood_model: {
            id: 'product_registration.model_examples.kenwood',
            defaultMessage:
                'Examples of valid Model Numbers: <i>HMP40.000GR, KMX750ARD, FDM73.850SS</i>',
        },
        braun_model: {
            id: 'product_registration.model_examples.braun',
            defaultMessage:
                'Examples of valid Model Numbers: <i>HB901-MQ9195XLI, CG9167, IS7298CP</i>',
        },
        nutribullet_model: {
            id: 'product_registration.model_examples.nutribullet',
            defaultMessage: 'Examples of valid Model Numbers: <i>NBJ500, NB1206DGCC, NBP003B</i>',
        },
        delonghi_model: {
            id: 'product_registration.model_examples',
            defaultMessage:
                'Examples of valid Model Numbers: <i>ECAM472.85.MB, EC9885.M, PACAP130IGENTLEJET</i>',
        },
    })

    const {
        skuImage,
        modelNumberImage,
        skuTitle,
        skuDescription,
        modelTitle,
        modelDescription,
        webPageSchemaMarkup,
    } = useProductRegistrationData()

    const SearchBar = (props) => {
        const intl = useIntl()
        const placeholder = intl.formatMessage({
            id: 'product_registration.search',
            defaultMessage: 'Search by SKU or Model Number',
        })
        return (
            <Search
                ariaLabel={placeholder}
                searchInputPlaceholder={placeholder}
                variant={'productSearch'}
                setSearchResults={setSearchResults}
                isProductRegistration
                setProductSelect={setProductSelect}
                setSuggestionRegistration={setSuggestionRegistration}
                shouldRemoveLabel
                showSpellingMessage={
                    searchResults?.hits === undefined &&
                    searchResults?.length !== 0 &&
                    !suggestionRegistration?.productId
                }
                {...props}
            />
        )
    }

    useEffect(() => {
        if (
            (searchResults &&
                productSelect.length !== 0 &&
                searchResults?.hits !== undefined &&
                searchResults?.length !== 0) ||
            (suggestionRegistration?.productId && searchResults && productSelect)
        ) {
            navigate(
                `${routingObject.PRODUCT_REGISTRATION_PATH}/select?text=` +
                    productSelect.replaceAll(' ', '-'),
            )
        }
    }, [searchResults, productSelect, suggestionRegistration])

    return (
        <Flex {...styles.container} layerStyle={'page'}>
            <Helmet>
                <script type="application/ld+json">
                    {JSON.stringify(webPageSchemaMarkup, null, 2)}
                </script>
            </Helmet>
            <Flex {...styles.headerStepperContainer}>
                <ProductRegistrationHeader index={1} variant={'product'} />
                <ProductRegistrationStepper index={1} />
            </Flex>
            <Flex {...styles.bottomContainer}>
                <Flex {...styles.labelSearchOuterContainer}>
                    <Flex {...styles.labelSearchInnerContainer}>
                        <Text {...styles.labelSearchTitle}>
                            <FormattedMessage
                                id="product_registration.enter_sku_or_model"
                                defaultMessage="Enter the SKU code or Model Number of your product"
                            />
                        </Text>
                        <Text {...styles.labelSearchDescription}>
                            <FormattedMessage
                                id="product_registration.no_sku_enter_model"
                                defaultMessage="Don't have the SKU code? Enter the Model Number of the product you want to register."
                            />
                        </Text>
                        <Box {...styles.searchBarOuterContainer}>
                            <Box {...styles.searchBarContainer}>
                                <SearchBar />
                            </Box>
                            <Text {...styles.exampleLabel}>
                                <FormattedMessage
                                    {...(isKenwood
                                        ? labels.kenwood_sku
                                        : isBraun
                                        ? labels.braun_sku
                                        : isNutribullet
                                        ? labels.nutribullet_sku
                                        : labels.delonghi_sku)}
                                    values={{
                                        i: (chunks) => <i>{chunks}</i>,
                                    }}
                                />
                            </Text>
                            <Text {...styles.exampleLabel}>
                                <FormattedMessage
                                    {...(isKenwood
                                        ? labels.kenwood_model
                                        : isBraun
                                        ? labels.braun_model
                                        : isNutribullet
                                        ? labels.nutribullet_model
                                        : labels.delonghi_model)}
                                    values={{
                                        i: (chunks) => <i>{chunks}</i>,
                                    }}
                                />
                            </Text>
                        </Box>
                    </Flex>
                </Flex>
                <Flex {...styles.infoBannerBox}>
                    <ProductRegistrationImageContainer
                        image={skuImage}
                        title={skuTitle}
                        description={skuDescription}
                        variant="productSearch"
                    />
                    <ProductRegistrationImageContainer
                        image={modelNumberImage}
                        title={modelTitle}
                        description={modelDescription}
                        variant="productSearch"
                    />
                </Flex>
            </Flex>
        </Flex>
    )
}

export default ProductSearch
